import Breadcrumbs, { BreadcrumbsItem } from '@atlaskit/breadcrumbs';
import Button from '@atlaskit/button/new';
import Spinner from '@atlaskit/spinner';
import TextField from '@atlaskit/textfield';
import PageHeader from '@atlaskit/page-header';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
  ApiRequestError,
  supplyPartnerServices,
  type SpCompanyDetails,
  type SpPaymentDetails,
  type SpPocDetails
} from '@shared/api';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { setSupplyPartnerProfile } from '@shared/store/partnerSlice';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

type EditableFieldValue = string | number | undefined;

type DetailField<TValue extends object> = {
  key: keyof TValue;
  label: string;
  type?: 'text' | 'number' | 'email';
};

interface DetailSectionCardProps<TValue extends object> {
  title: string;
  value?: TValue;
  fallbackValue?: TValue;
  fields: DetailField<TValue>[];
  isSaving: boolean;
  onSave: (value: TValue) => void;
  helperMessage?: string;
  errorMessage?: string;
}

const cardStyles = {
  background: token('color.background.input'),
  border: `1px solid ${token('color.border')}`,
  borderRadius: '18px',
  padding: token('space.300')
} as const;

const dividerStyles = {
  background: token('color.border'),
  height: '1px',
  width: '100%'
} as const;

const formatAuditDate = (value?: string) =>
  value
    ? new Intl.DateTimeFormat('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(new Date(value))
    : 'Not available';

function DetailSectionCard<TValue extends object>({
  title,
  value,
  fallbackValue,
  fields,
  isSaving,
  onSave,
  helperMessage,
  errorMessage
}: DetailSectionCardProps<TValue>) {
  const resolvedValue = value ?? fallbackValue;
  const [isEditing, setIsEditing] = useState(() => !value && Boolean(fallbackValue));
  const [draft, setDraft] = useState<TValue | undefined>(resolvedValue);

  useEffect(() => {
    setDraft(resolvedValue);
    setIsEditing(!value && Boolean(fallbackValue));
  }, [fallbackValue, resolvedValue, value]);

  if (!resolvedValue || !draft) {
    return (
      <Box style={cardStyles}>
        <Stack space="space.150">
          <Text size="large" weight="medium">
            {title}
          </Text>
          {errorMessage ? (
            <Text color="color.text.danger">{errorMessage}</Text>
          ) : (
            <Inline spread="space-between" alignBlock="center">
              <Text color="color.text.subtle">Loading details</Text>
              <Spinner size="medium" />
            </Inline>
          )}
        </Stack>
      </Box>
    );
  }

  return (
    <Box style={cardStyles}>
      <Stack space="space.250">
        <Inline spread="space-between" alignBlock="center">
          <Text size="large" weight="medium">
            {title}
          </Text>
          {isEditing ? (
            <Inline space="space.100">
              <Button
                appearance="primary"
                isLoading={isSaving}
                onClick={() => onSave(draft)}
              >
                Save
              </Button>
              <Button
                appearance="danger"
                isDisabled={isSaving}
                onClick={() => {
                  setDraft(value);
                  setIsEditing(false);
                }}
              >
                Cancel
              </Button>
            </Inline>
          ) : (
            <Button appearance="subtle" onClick={() => setIsEditing(true)}>
              Edit
            </Button>
          )}
        </Inline>

        <Box style={dividerStyles} />

        {helperMessage ? <Text color="color.text.subtle">{helperMessage}</Text> : null}

        <div
          style={{
            display: 'grid',
            gap: token('space.250'),
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))'
          }}
        >
          {fields.map((field) => {
            const fieldValue = draft[field.key] as EditableFieldValue;

            return (
              <div key={String(field.key)}>
                <Stack space="space.050">
                  <Text color="color.text.subtle" size="small">
                    {field.label}
                  </Text>
                  {isEditing ? (
                    <TextField
                      aria-label={field.label}
                      type={field.type ?? 'text'}
                      value={String(fieldValue ?? '')}
                      onChange={(event) => {
                        const nextRawValue = event.currentTarget.value;
                        setDraft((currentDraft) => {
                          if (!currentDraft) {
                            return currentDraft;
                          }

                          return {
                            ...currentDraft,
                            [field.key]:
                              field.type === 'number' ? Number(nextRawValue || 0) : nextRawValue
                          };
                        });
                      }}
                    />
                  ) : (
                    <Text size="medium">
                      {field.type === 'number'
                        ? String(fieldValue ?? 0)
                        : String(fieldValue || 'Not provided')}
                    </Text>
                  )}
                </Stack>
              </div>
            );
          })}
        </div>

        {'updatedAt' in resolvedValue && 'updatedBy' in resolvedValue ? (
          <Text color="color.text.subtle" size="small">
            Last updated {formatAuditDate(String(resolvedValue.updatedAt))} by{' '}
            {String(resolvedValue.updatedBy || 'system')}
          </Text>
        ) : null}
      </Stack>
    </Box>
  );
}

const isMissingDetailsError = (error: unknown) =>
  error instanceof ApiRequestError && error.status === 400;

const getErrorMessage = (error: unknown) =>
  error instanceof Error ? error.message : 'Unable to load details.';

const pocFields: DetailField<SpPocDetails>[] = [
  { key: 'spPocFirstName', label: 'First Name' },
  { key: 'spPocLastName', label: 'Last Name' },
  { key: 'spPocEmailAddress', label: 'Email Address', type: 'email' },
  { key: 'spPocMobileNumber', label: 'Phone Number' }
];

const companyFields: DetailField<SpCompanyDetails>[] = [
  { key: 'spCompanyName', label: 'Company Name' },
  { key: 'spCompanyAddress', label: 'Address' },
  { key: 'spCompanyCity', label: 'City' },
  { key: 'spCompanyState', label: 'State' },
  { key: 'spCompanyCountry', label: 'Country' },
  { key: 'spCompanyZipCode', label: 'ZIP Code' }
];

const paymentFields: DetailField<SpPaymentDetails>[] = [
  { key: 'spPaymentMethod', label: 'Payment Method' },
  { key: 'spPaymentCurrency', label: 'Payment Currency' },
  { key: 'spPaymentDays', label: 'Payment Days', type: 'number' },
  { key: 'spBankName', label: 'Bank Name' },
  { key: 'spBankAccountName', label: 'Bank Account Name' },
  { key: 'spBankAccountNumber', label: 'Bank Account Number' },
  { key: 'spBankAccountType', label: 'Bank Account Type' },
  { key: 'spBankRecipientType', label: 'Recipient Type' },
  { key: 'spBankIfscCode', label: 'IFSC Code' },
  { key: 'spBankSwiftCode', label: 'Swift Code' },
  { key: 'spBankIbanNumber', label: 'IBAN Number' },
  { key: 'spBankPhoneNumber', label: 'Bank Phone Number' },
  { key: 'spBankStreetAddress', label: 'Bank Address Line 1' },
  { key: 'spBankAddressLine2', label: 'Bank Address Line 2' },
  { key: 'spBankBranchName', label: 'Branch Name' },
  { key: 'spBankCityName', label: 'Bank City' },
  { key: 'spBankPinCode', label: 'Bank ZIP Code' },
  { key: 'spBankCountry', label: 'Bank Country' },
  { key: 'spFinancePocFirstName', label: 'Finance POC First Name' },
  { key: 'spFinancePocLastName', label: 'Finance POC Last Name' },
  { key: 'spFinancePocEmailAddress', label: 'Finance POC Email', type: 'email' },
  { key: 'spFinancePocMobileNumber', label: 'Finance POC Phone Number' }
];

export default function SupplyPartnerDetailsPage() {
  const { spId = '' } = useParams();
  const dispatch = useAppDispatch();
  const supplyPartnerProfile = useAppSelector((state) =>
    spId ? state.partner.supplyPartnerProfiles[spId] : undefined
  );
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const breadcrumbs = (
    <Breadcrumbs>
      <BreadcrumbsItem text="Dashboard" key="Dashboard" onClick={() => navigate('/home')} />
      <BreadcrumbsItem
        text="Supply Partners"
        key="Supply Partners"
        onClick={() => navigate('/supply-partners')}
      />
      <BreadcrumbsItem text={spId} key={spId} />
    </Breadcrumbs>
  );

  const pocQuery = useQuery({
    queryKey: ['supplyPartnerPocDetails', spId],
    queryFn: (): Promise<SpPocDetails> => supplyPartnerServices.getSpPocDetails(spId),
    enabled: Boolean(spId)
  });

  const companyQuery = useQuery({
    queryKey: ['supplyPartnerCompanyDetails', spId],
    queryFn: (): Promise<SpCompanyDetails> => supplyPartnerServices.getSpCompanyDetails(spId),
    enabled: Boolean(spId)
  });

  const paymentQuery = useQuery({
    queryKey: ['supplyPartnerPaymentDetails', spId],
    queryFn: (): Promise<SpPaymentDetails> => supplyPartnerServices.getSpPaymentDetails(spId),
    enabled: Boolean(spId)
  });

  const pocMutation = useMutation({
    mutationFn: (payload: SpPocDetails) => supplyPartnerServices.updateSpPocDetails(spId, payload),
    onSuccess: async () => {
      const profile = await supplyPartnerServices.getSpProfileDetails(spId);
      dispatch(setSupplyPartnerProfile(profile));
      void queryClient.invalidateQueries({ queryKey: ['supplyPartnerPocDetails', spId] });
    }
  });

  const companyMutation = useMutation({
    mutationFn: (payload: SpCompanyDetails) =>
      supplyPartnerServices.updateSpCompanyDetails(spId, payload),
    onSuccess: async () => {
      const profile = await supplyPartnerServices.getSpProfileDetails(spId);
      dispatch(setSupplyPartnerProfile(profile));
      void queryClient.invalidateQueries({ queryKey: ['supplyPartnerCompanyDetails', spId] });
    }
  });

  const paymentMutation = useMutation({
    mutationFn: (payload: SpPaymentDetails) =>
      supplyPartnerServices.updateSpPaymentDetails(spId, payload),
    onSuccess: async () => {
      const profile = await supplyPartnerServices.getSpProfileDetails(spId);
      dispatch(setSupplyPartnerProfile(profile));
      void queryClient.invalidateQueries({ queryKey: ['supplyPartnerPaymentDetails', spId] });
    }
  });

  const isPageLoading =
    pocQuery.isLoading || companyQuery.isLoading || paymentQuery.isLoading;

  const subtitle = useMemo(() => {
    if (!supplyPartnerProfile) {
      return '';
    }

    return `${supplyPartnerProfile.spName} • ${supplyPartnerProfile.status}`;
  }, [supplyPartnerProfile]);

  const pocFallbackValue = useMemo<SpPocDetails | undefined>(() => {
    if (!isMissingDetailsError(pocQuery.error)) {
      return undefined;
    }

    return {
      spId,
      spPocFirstName: '',
      spPocLastName: '',
      spPocEmailAddress: '',
      spPocMobileNumber: ''
    };
  }, [pocQuery.error, spId]);

  const companyFallbackValue = useMemo<SpCompanyDetails | undefined>(() => {
    if (!isMissingDetailsError(companyQuery.error)) {
      return undefined;
    }

    return {
      spId,
      spCompanyName: supplyPartnerProfile?.spName ?? '',
      spCompanyAddress: '',
      spCompanyCity: '',
      spCompanyState: '',
      spCompanyCountry: '',
      spCompanyZipCode: ''
    };
  }, [companyQuery.error, spId, supplyPartnerProfile?.spName]);

  const paymentFallbackValue = useMemo<SpPaymentDetails | undefined>(() => {
    if (!isMissingDetailsError(paymentQuery.error)) {
      return undefined;
    }

    return {
      spId,
      spBankName: '',
      spPaymentMethod: '',
      spPaymentCurrency: '',
      spPaymentDays: 0,
      spBankAccountNumber: '',
      spBankAccountName: '',
      spBankRecipientType: '',
      spBankIfscCode: '',
      spBankPhoneNumber: '',
      spBankSwiftCode: '',
      spBankStreetAddress: '',
      spBankAddressLine2: '',
      spBankBranchName: '',
      spBankCityName: '',
      spBankPinCode: '',
      spBankAccountType: '',
      spBankCountry: '',
      spBankIbanNumber: '',
      spFinancePocFirstName: '',
      spFinancePocLastName: '',
      spFinancePocEmailAddress: '',
      spFinancePocMobileNumber: ''
    };
  }, [paymentQuery.error, spId]);

  if (!spId) {
    return (
      <Stack space="space.300" xcss={{ width: '100%' }}>
        <PageHeader breadcrumbs={breadcrumbs}>Supply Partner Details</PageHeader>
        <Text>Supply partner ID not found.</Text>
      </Stack>
    );
  }

  return (
    <Stack space="space.300" xcss={{ width: '100%' }}>
      <PageHeader breadcrumbs={breadcrumbs}>
        {supplyPartnerProfile?.spName ?? `Supply Partner ${spId}`}
      </PageHeader>

      <Box
        style={{
          alignItems: 'center',
          background: token('color.background.neutral.subtle'),
          borderRadius: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          padding: token('space.250')
        }}
      >
        <div>
          <Text size="large" weight="bold">
            {spId}
          </Text>
          <Text color="color.text.subtle">{subtitle || 'Loading partner profile'}</Text>
        </div>
      </Box>

      {isPageLoading ? (
        <Box
          style={{
            alignItems: 'center',
            display: 'flex',
            height: '280px',
            justifyContent: 'center'
          }}
        >
          <Spinner size="large" />
        </Box>
      ) : (
        <Stack space="space.250">
          <DetailSectionCard
            title="Contact Information"
            value={pocQuery.data}
            fallbackValue={pocFallbackValue}
            fields={pocFields}
            isSaving={pocMutation.isPending}
            onSave={(value) => pocMutation.mutate(value)}
            helperMessage={
              pocFallbackValue
                ? 'Contact details are not available yet. Add them below to create the record.'
                : undefined
            }
            errorMessage={
              pocQuery.error && !pocFallbackValue ? getErrorMessage(pocQuery.error) : undefined
            }
          />
          <DetailSectionCard
            title="Company Details"
            value={companyQuery.data}
            fallbackValue={companyFallbackValue}
            fields={companyFields}
            isSaving={companyMutation.isPending}
            onSave={(value) => companyMutation.mutate(value)}
            helperMessage={
              companyFallbackValue
                ? 'Company details are not available yet. Add them below to create the record.'
                : undefined
            }
            errorMessage={
              companyQuery.error && !companyFallbackValue
                ? getErrorMessage(companyQuery.error)
                : undefined
            }
          />
          <DetailSectionCard
            title="Payment Details"
            value={paymentQuery.data}
            fallbackValue={paymentFallbackValue}
            fields={paymentFields}
            isSaving={paymentMutation.isPending}
            onSave={(value) => paymentMutation.mutate(value)}
            helperMessage={
              paymentFallbackValue
                ? 'Payment details are not available yet. Add them below to create the record.'
                : undefined
            }
            errorMessage={
              paymentQuery.error && !paymentFallbackValue
                ? getErrorMessage(paymentQuery.error)
                : undefined
            }
          />
        </Stack>
      )}
    </Stack>
  );
}
